/**
 * Push Permission Banner Component
 * Prompts users to enable push notifications with contextual messaging
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, Smartphone, CheckCircle, XCircle } from 'lucide-react';
import { getNotificationService } from '../../services/notification.service';
import { cn } from '../../utils/cn';

interface PushPermissionBannerProps {
  className?: string;
  showOnlyIfNeeded?: boolean;
  onPermissionGranted?: () => void;
  onPermissionDenied?: () => void;
  onDismiss?: () => void;
}

export const PushPermissionBanner: React.FC<PushPermissionBannerProps> = ({
  className,
  showOnlyIfNeeded = true,
  onPermissionGranted,
  onPermissionDenied,
  onDismiss
}) => {
  // Read localStorage synchronously on mount to prevent banner flash
  const [dismissed, setDismissed] = useState(() =>
    localStorage.getItem('push-permission-banner-dismissed') === 'true'
  );
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState<'default' | 'granted' | 'denied'>('default');

  const notificationService = getNotificationService();

  useEffect(() => {
    // If already dismissed via localStorage, skip the async check entirely
    if (dismissed) return;
    checkPermissionStatus();
  }, []);

  const checkPermissionStatus = async () => {
    try {
      // Check localStorage dismissal FIRST — fast path, no async
      const dismissedKey = 'push-permission-banner-dismissed';
      const wasDismissed = localStorage.getItem(dismissedKey) === 'true';
      if (showOnlyIfNeeded && wasDismissed) {
        setVisible(false);
        return;
      }

      // Check if push notifications are supported
      if (!notificationService.isSupported()) {
        setVisible(false);
        return;
      }

      // Check current permission status
      const hasPermission = await notificationService.checkPermission();
      const isSubscribed = await notificationService.isPushEnabled();

      if (hasPermission && isSubscribed) {
        setPermissionStatus('granted');
        setVisible(false);
      } else if (hasPermission && !isSubscribed) {
        // Has permission but not subscribed - try to subscribe
        await notificationService.enablePushNotifications();
        setPermissionStatus('granted');
        setVisible(false);
      } else {
        setPermissionStatus('default');

        if (!showOnlyIfNeeded || !dismissed) {
          setVisible(true);
        }
      }
    } catch (error) {
      console.error('Failed to check permission status:', error);
      // On error, respect dismissal — don't show a broken banner
    }
  };

  const handleEnableNotifications = async () => {
    setLoading(true);
    
    try {
      const success = await notificationService.enablePushNotifications();
      
      if (success) {
        setPermissionStatus('granted');
        setVisible(false);
        onPermissionGranted?.();
        
        // Clear dismissed flag
        localStorage.removeItem('push-permission-banner-dismissed');
      } else {
        setPermissionStatus('denied');
        onPermissionDenied?.();
      }
    } catch (error) {
      console.error('Failed to enable push notifications:', error);
      setPermissionStatus('denied');
      onPermissionDenied?.();
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    setDismissed(true);
    
    // Remember dismissal
    localStorage.setItem('push-permission-banner-dismissed', 'true');
    
    onDismiss?.();
  };

  const handleNotNow = () => {
    handleDismiss();
  };

  if (!visible || permissionStatus === 'granted') {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -100 }}
        className={cn(
          'relative bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg',
          className
        )}
      >
        <div className="px-4 py-4 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Bell className="h-6 w-6" />
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium">
                  Stay Updated with Push Notifications
                </h3>
                <p className="mt-1 text-sm opacity-90">
                  Get instant notifications for consultations, commission updates, and important messages.
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {permissionStatus === 'denied' ? (
                <div className="flex items-center text-red-200">
                  <XCircle className="h-5 w-5 mr-2" />
                  <span className="text-sm">Permission denied</span>
                </div>
              ) : (
                <>
                  <button
                    onClick={handleEnableNotifications}
                    disabled={loading}
                    className="inline-flex items-center px-4 py-2 border border-white/30 rounded-md text-sm font-medium text-white bg-white/20 hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Enabling...
                      </>
                    ) : (
                      <>
                        <Smartphone className="h-4 w-4 mr-2" />
                        Enable
                      </>
                    )}
                  </button>

                  <button
                    onClick={handleNotNow}
                    className="text-sm text-white/80 hover:text-white underline"
                  >
                    Not now
                  </button>
                </>
              )}

              <button
                onClick={handleDismiss}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

interface PushPermissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPermissionGranted?: () => void;
  onPermissionDenied?: () => void;
}

export const PushPermissionModal: React.FC<PushPermissionModalProps> = ({
  isOpen,
  onClose,
  onPermissionGranted,
  onPermissionDenied
}) => {
  const [loading, setLoading] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState<'default' | 'granted' | 'denied'>('default');

  const notificationService = getNotificationService();

  const handleEnableNotifications = async () => {
    setLoading(true);
    
    try {
      const success = await notificationService.enablePushNotifications();
      
      if (success) {
        setPermissionStatus('granted');
        onPermissionGranted?.();
        setTimeout(onClose, 1500); // Close after showing success
      } else {
        setPermissionStatus('denied');
        onPermissionDenied?.();
      }
    } catch (error) {
      console.error('Failed to enable push notifications:', error);
      setPermissionStatus('denied');
      onPermissionDenied?.();
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
      >
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
            <Bell className="h-6 w-6 text-blue-600" />
          </div>

          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Enable Push Notifications
          </h3>

          <p className="text-sm text-gray-500 mb-6">
            Stay informed with real-time notifications for consultations, commission updates,
            and important messages even when the app is closed.
          </p>

          {permissionStatus === 'granted' && (
            <div className="flex items-center justify-center text-green-600 mb-4">
              <CheckCircle className="h-6 w-6 mr-2" />
              <span className="font-medium">Notifications Enabled!</span>
            </div>
          )}

          {permissionStatus === 'denied' && (
            <div className="flex items-center justify-center text-red-600 mb-4">
              <XCircle className="h-6 w-6 mr-2" />
              <span className="font-medium">Permission Denied</span>
            </div>
          )}

          <div className="space-y-3">
            <div className="flex items-center text-sm text-gray-600">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
              Appointment reminders and confirmations
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
              Commission status updates and completions
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
              Order updates and delivery notifications
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
              Important system announcements
            </div>
          </div>
        </div>

        <div className="mt-8 flex space-x-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          >
            Maybe Later
          </button>
          
          <button
            onClick={handleEnableNotifications}
            disabled={loading || permissionStatus === 'granted'}
            className="flex-1 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Enabling...
              </div>
            ) : permissionStatus === 'granted' ? (
              'Enabled!'
            ) : (
              'Enable Notifications'
            )}
          </button>
        </div>

        {permissionStatus === 'denied' && (
          <p className="mt-4 text-xs text-gray-500 text-center">
            To enable notifications, please allow them in your browser settings and refresh this page.
          </p>
        )}
      </motion.div>
    </div>
  );
};

export default PushPermissionBanner;